import GeoNerdNavigation from "./services/GeoNerdNavigation.js";
import PageTransition from "./services/PageTransition.js";
import HomeCards from "./pages/HomeCards.js";
import Scores from "./pages/Scores.js";
import CapitalNerdClassic from "./games/CapitalNerdClassic.js";
import CapitalNerdHard from "./games/CapitalNerdHard.js";
import CapitalNerdReverse from "./games/CapitalNerdReverse.js";
import CountryNerd from "./games/CountryNerd.js";
import FlagNerdClassic from "./games/FlagNerdClassic.js";
import FlagNerdHard from "./games/FlagNerdHard.js";
import FlagNerdTricky from "./games/FlagNerdTricky.js";
import StringUtils from "./utils/StringUtils.js";
import LanguageManager from "./utils/LanguageManager.js";
import MapNerdController from "./pages/MapNerdController.js";
import UsStatesClassic from "./games/UsStatesClassic.js";
import UsStatesHard from "./games/UsStatesHard.js";
import UsStatesList from "./games/UsStatesList.js";
import FlagNerdSpeedRun from "./games/FlagNerdSpeedRun.js";
import CapitalNerdSpeedRun from "./games/CapitalNerdSpeedRun.js";
import Training from "./pages/Training.js";
import HeaderMenu from "./services/HeaderMenu.js";
import GameInfo from "./services/GameInfo.js";
import SoundManager from "./utils/SoundManager.js";
import DailyChallenge from "./games/DailyChallenge.js";
import ShapeNerd from "./games/ShapeNerd.js";
import ShapeNerdHard from "./games/ShapeNerdHard.js";
import BorderNerd from "./games/BorderNerd.js";
import FriendChallenge from "./games/FriendChallenge.js";
import AccountSync from "./services/AccountSync.js";
import AccountMenu from "./services/AccountMenu.js";
import NotificationsUI from "./services/NotificationsUI.js";
import ResetPassword from "./pages/ResetPassword.js";
import Admin from "./pages/Admin.js";
import Leaderboard from "./pages/Leaderboard.js";
import AvatarPage from "./pages/AvatarPage.js";
import AuthPage from "./pages/AuthPage.js";
import Profile from "./pages/Profile.js";
import ProfileSettings from "./pages/ProfileSettings.js";
import InProgress from "./pages/InProgress.js";
import UnsubscribePage from "./pages/UnsubscribePage.js";
import PassportPage from "./pages/PassportPage.js";
import ReviewPage from "./pages/ReviewPage.js";
import { captureInstallPrompt } from "./services/InstallPrompt.js";
import * as FlagPreloader from "./utils/FlagPreloader.js";
import PlayerIdentity from "./services/PlayerIdentity.js";

class GeoNerdApp {
	constructor() {
		// Single source of truth for the app
		window.geoNerdApp = this;

		document.addEventListener("DOMContentLoaded", () => {
			const soundBtn = document.querySelector(".btn-sound-toggle");
			if (soundBtn) {
				if (SoundManager.muted) soundBtn.classList.add("muted");
				soundBtn.addEventListener("click", () => {
					const muted = SoundManager.toggle();
					soundBtn.classList.toggle("muted", muted);
				});
			}

			// Initialize language manager
			this.languageManager = new LanguageManager();

			new HeaderMenu();
			new GameInfo();

			// Account: kick off the session/sync bootstrap immediately. When logged
			// in, this pulls the online save and merges it into localStorage BEFORE
			// the pages read it (page modules below await accountReady). No-op /
			// fast-resolves when anonymous or the API is unreachable.
			this.accountReady = AccountSync.init();
			// Visite du jour, pour la mesure de rétention (au plus un appel par jour).
			this.accountReady.then(() => PlayerIdentity.markActiveToday()).catch(() => {});
			new AccountMenu();
			new NotificationsUI();
			if (document.querySelector(".page.reset-password")) new ResetPassword();
			if (document.querySelector(".page.geoadmin")) new Admin();
			if (document.querySelector(".page.leaderboard")) new Leaderboard();
			if (document.querySelector(".page.avatar")) new AvatarPage();
			// Pages de compte : aucune n'a besoin des données pays.
			if (document.querySelector(".page.auth-page")) new AuthPage();
			if (document.querySelector(".page.unsubscribe-page")) new UnsubscribePage();
			if (document.querySelector(".page.profile")) new Profile();
			if (document.querySelector(".page.profile")) new ProfileSettings();

			// Page enter/exit transitions are page-agnostic and independent of the
			// country data, so wire them up immediately (the enter reveal must not
			// be gated by the fetch, or a failed load would leave a page hidden).
			new PageTransition();

			// The home page is static (no country data needed), so play its
			// sequenced entrance right away rather than waiting on — or being
			// gated by the success of — the country fetch.
			if (document.querySelector(".page.home")) this.homeCards = new HomeCards();

			this.countries = [];
			this.easyCountries = [];
			this.countriesByLetter = {};
			// Derived from the loaded data (see loadCountries) — no hardcoded alphabet.
			this.letters = [];
			this.loadError = false;
			// Continent assignment per ISO code (af -> "AS", …). Drives Map Nerd's
			// per-continent scopes and the exploration progression.
			this.continents = {};
			this.easyCountriesByContinent = {};
			this.continentsLoadError = false;
			this.usStates = [];
			this.usStatesLoadError = false;
			const countriesReady = this.loadCountries();
			const continentsReady = this.loadContinents();
			this.continentsReady = continentsReady;
			const usStatesReady = this.loadUsStates();

			Promise.all([countriesReady, usStatesReady, this.accountReady]).then(() => {
				// Prefetching all flag images is real CPU/network work. Game pages only
				// wait for the current question's flag (FlagPreloader.load), and want the
				// rest streaming in right away for the next ones — but the home page doesn't render
				// any flag and has its own GSAP entrance animation, so there defer the
				// preload to idle time to avoid competing with it right after load.
				if (document.querySelector(".page.home")) {
					const preloadFlags = () => FlagPreloader.start(this.countries);
					if (window.requestIdleCallback) {
						window.requestIdleCallback(preloadFlags, { timeout: 2000 });
					} else {
						setTimeout(preloadFlags, 1000);
					}
				} else {
					FlagPreloader.start(this.countries);
				}
				new GeoNerdNavigation();
				if (this.loadError) { this.showDataError(); return; }
				if (this.homeCards) this.homeCards.renderPersonal();
				if (!this.usStatesLoadError) {
					if (document.querySelector(".page.us-states-classic")) new UsStatesClassic();
					if (document.querySelector(".page.us-states-hard")) new UsStatesHard();
					if (document.querySelector(".page.us-states-list")) new UsStatesList();
				}
				if (document.querySelector(".page.capital-nerd-classic")) new CapitalNerdClassic();
				if (document.querySelector(".page.capital-nerd-hard")) new CapitalNerdHard();
				if (document.querySelector(".page.capital-nerd-reverse")) new CapitalNerdReverse();
				if (document.querySelector(".page.country-nerd:not(.country-nerd-hard)")) new CountryNerd("classic");
				if (document.querySelector(".page.country-nerd-hard")) new CountryNerd("hard");
				if (document.querySelector(".page.flag-nerd-classic")) new FlagNerdClassic();
				if (document.querySelector(".page.flag-nerd-hard")) new FlagNerdHard();
				if (document.querySelector(".page.flag-nerd-tricky")) new FlagNerdTricky();
				if (document.querySelector(".page.flag-nerd-speed-run")) new FlagNerdSpeedRun();
				if (document.querySelector(".page.capital-nerd-speed-run")) new CapitalNerdSpeedRun();
				if (document.querySelector(".page.daily-challenge:not(.daily-challenge-hard)")) new DailyChallenge("classic");
				if (document.querySelector(".page.daily-challenge-hard")) new DailyChallenge("hard");
				// Le Lab
				if (document.querySelector(".page.shape-nerd")) new ShapeNerd();
				if (document.querySelector(".page.shape-nerd-hard")) new ShapeNerdHard();
				if (document.querySelector(".page.border-nerd")) new BorderNerd();
				if (document.querySelector(".page.friend-challenge")) new FriendChallenge();
				if (document.querySelector(".page.pays") || document.querySelector(".page.training-map") || document.querySelector(".page.training-map-us")) new Training();
			});

			Promise.all([countriesReady, continentsReady, this.accountReady]).then(() => {
				if (this.loadError) { this.showDataError(); return; }
				// Tag the easy countries with their continent and bucket them, so the
				// scoped Map Nerd games and the Scores exploration panel can filter.
				this._indexContinents();
				if (document.querySelector(".page.map-nerd-classic")) new MapNerdController("classic");
				if (document.querySelector(".page.map-nerd-hard")) new MapNerdController("hard");
				// Scores needs both lists loaded so the denominators reflect the real counts.
				if (document.querySelector(".profile-progress")) new Scores();
				// Idem : la progression d'une partie en cours se lit sur le total du
				// mode, y compris par continent (donc après l'indexation ci-dessus).
				if (document.querySelector(".page.in-progress")) new InProgress();
				if (document.querySelector(".page.passport")) new PassportPage();
				if (document.querySelector(".page.review")) new ReviewPage();
			});
		});
	}

	loadCountries() {
		return fetch(this.languageManager.getCountriesDataPath())
			.then(response => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.json();
			})
			.then(data => {
				data.forEach(country => {
					const letter = StringUtils.sanitize(country.name.substring(0, 1));
					if (!this.countriesByLetter[letter]) {
						this.countriesByLetter[letter] = [];
					}
					const entry = {
						sanitize: StringUtils.sanitize(country.name),
						sanitizeAliases: (country.aliases || []).map(a => StringUtils.sanitize(a)),
						name: country.name,
						code: country.code,
						capital: country.capital
					};
					this.countriesByLetter[letter].push(entry);
					this.countries.push(entry);
					// Map Nerd only plays the subset flagged eligible (micro-states excluded).
					if (country.mapEligible !== false) {
						this.easyCountries.push(entry);
					}
				});
				// Letters are whatever the data actually starts with (sorted), not a fixed list.
				this.letters = Object.keys(this.countriesByLetter).sort();
			})
			.catch(e => {
				this.loadError = true;
				console.error("Failed to load countries:", e);
			});
	}

	loadUsStates() {
		return fetch(this.languageManager.getUsStatesDataPath())
			.then(response => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.json();
			})
			.then(data => {
				this.usStates = data.map(state => ({
					sanitize: StringUtils.sanitize(state.name),
					name: state.name,
					code: state.code
				}));
			})
			.catch(e => {
				this.usStatesLoadError = true;
				console.error("Failed to load US states:", e);
			});
	}

	loadContinents() {
		return fetch("/data/continents.json")
			.then(response => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.json();
			})
			.then(data => {
				// `_meta` only documents the transcontinental choices — skip it.
				Object.keys(data).forEach(code => {
					if (code.startsWith("_")) return;
					this.continents[code] = data[code].toLowerCase();
				});
			})
			.catch(e => {
				this.continentsLoadError = true;
				console.error("Failed to load continents:", e);
			});
	}

	/**
	 * Stamp each playable (easy) country with its continent and bucket them into
	 * `easyCountriesByContinent` (keyed by lowercase continent code: af/as/eu/na/sa/oc).
	 * Any easy country missing from continents.json is logged so the gap is visible.
	 */
	_indexContinents() {
		this.easyCountriesByContinent = {};
		this.easyCountries.forEach(c => {
			const continent = this.continents[c.code];
			if (!continent) {
				console.warn(`No continent mapping for country code "${c.code}"`);
				return;
			}
			c.continent = continent;
			if (!this.easyCountriesByContinent[continent]) {
				this.easyCountriesByContinent[continent] = [];
			}
			this.easyCountriesByContinent[continent].push(c);
		});
	}

	/**
	 * Show a non-blocking, user-facing banner when country data fails to load,
	 * instead of failing silently in the console. Idempotent.
	 */
	showDataError() {
		if (this._dataErrorShown) return;
		this._dataErrorShown = true;

		const banner = document.createElement("div");
		banner.className = "data-error-banner";
		banner.setAttribute("role", "alert");
		banner.textContent = this.languageManager.t("errors.dataLoad");
		document.body.prepend(banner);
	}

	shareResult(btn, gameName, modeName, score, emoji, pageHash = "") {
		const langPrefix = this.languageManager.currentLang === 'en' ? '/en' : '';
		const url = `https://geo-nerd.com${langPrefix}${pageHash ? "/" + pageHash : ""}`;
		const text = `#GeoNerd | #${gameName.replace(/\s+/g, "")} 🌍\n\n${emoji} ${modeName}: ${score}\n\n${url}`;
		navigator.clipboard.writeText(text).then(() => {
			const textElement = btn.querySelector(".share-text");
			const originalText = textElement.textContent;
			textElement.textContent = this.languageManager.t("scores.copied");
			btn.classList.add("copied");
			setTimeout(() => {
				textElement.textContent = originalText;
				btn.classList.remove("copied");
			}, 2000);
		}).catch(() => {
			const textElement = btn.querySelector(".share-text");
			const originalText = textElement.textContent;
			textElement.textContent = this.languageManager.t("scores.copyFailed");
			setTimeout(() => {
				textElement.textContent = originalText;
			}, 2000);
		});
	}
}

if ('serviceWorker' in navigator) {
	navigator.serviceWorker.register('/sw.js').catch(() => {});
}

// L'invite d'installation part tôt (avant même DOMContentLoaded parfois) : on la
// garde pour la proposer plus tard, au bon moment (voir InstallPrompt.js).
captureInstallPrompt();

const geoNerdApp = new GeoNerdApp();
